<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { defineProps, computed, onMounted } from 'vue';
import { Banners, Modal, ButtonComponent } from '@/components';
import Pagination from '@/components/LightPagination.vue';
import EquipmentCard from '@/components/EquipmentCard.vue';
import { useScreenSize, useExchangeAccess } from '@/composables';
import EquipmentTable from '@/components/EquipmentTable.vue';
import EquipmentSkeletonTable from '@/components/EquipmentSkeletonTable.vue';
import EquipmentCardSkeleton from '@/components/EquipmentCardSkeleton.vue';
import EldSectionMessages from '@/components/EldSectionMessages.vue';
import EldProviderSummary from '@/components/EldProviderSummary.vue';

const store = useStore();
const router = useRouter();
const { screenSize } = useScreenSize();
const { bannerType, displayDismissButton } = useExchangeAccess();
const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

const isCarrierVehiclesResponseLoading = computed(
  () => store.state.myLoadsStore.equipmentTableVehicles.isLoading
);

const data = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.vehicles;
});
const pagination = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.pagination;
});

const fetchData = (page = 1) => {
  store.dispatch('myLoadsStore/getEquipmentTableCarrierVehicles', {
    page: page,
  });
};

fetchData();

onMounted(async () => {
  await store.dispatch(
    'onboardingPacket/getEldIntegrations',
    store.state.users?.currentUser?.company?.id
  );
});

const eldProviders = computed(() => {
  return store.state.onboardingPacket.eld_integrations;
});

const eldProvidersNotRevoked = computed(() => {
  return eldProviders.value?.filter((item) => item.revoked_at === null);
});

const displayEquipmentBanner = computed(() => {
  return store.state.users.featureFlags?.display_equipment_banner?.enabled;
});

const handleSelectPage = (page) => {
  fetchData(page);
};

defineProps({
  showDismissButtonProp: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);

const onDismiss = () => {
  emit('close');
};

const onResolveIssues = () => {
  router.push({ name: 'ELDConnections' });
};
</script>

<template>
  <Modal
    v-if="visible"
    :visible="visible"
    @click-outside-modal="emit('close')"
  >
    <section>
      <div
        v-if="isTabletOrMobile"
        class="h-screen w-screen"
      >
        <Banners>
          <div class="relative flex h-screen flex-col bg-steel-100">
            <div class="flex flex-col gap-4 overflow-y-auto p-4 pb-48">
              <EldSectionMessages
                v-if="displayEquipmentBanner"
                :status="bannerType"
              />
              <div class="flex flex-col flex-wrap gap-2">
                <EldProviderSummary
                  v-for="provider in eldProvidersNotRevoked"
                  :key="provider.id"
                  :eld-provider="provider"
                  :total-providers="eldProvidersNotRevoked?.length"
                />
              </div>
              <template v-if="isCarrierVehiclesResponseLoading">
                <EquipmentCardSkeleton
                  v-for="index in 5"
                  :key="index"
                />
              </template>
              <template v-else>
                <div
                  class="flex w-full flex-wrap items-center justify-center gap-4"
                >
                  <EquipmentCard
                    v-for="(item, index) in data"
                    :key="index"
                    :equipment="item"
                    :is-modal="true"
                  />
                </div>
              </template>
            </div>
          </div>
          <div class="sticky bottom-0">
            <div class="flex items-center justify-center bg-steel-100 py-3">
              <Pagination
                v-if="isTabletOrMobile"
                :selected-page="pagination.currentPage"
                :total-pages="pagination.totalPages"
                equivalent
                @select-page="handleSelectPage"
              ></Pagination>
            </div>
            <div class="flex flex-col gap-4 bg-white px-4 py-3 shadow-md">
              <ButtonComponent
                v-if="displayDismissButton || showDismissButtonProp"
                @click="onDismiss"
              >
                Dismiss
              </ButtonComponent>
              <ButtonComponent
                variant="secondary"
                @click="onResolveIssues"
              >
                Resolve issues
              </ButtonComponent>
            </div>
          </div>
        </Banners>
      </div>
      <div
        v-else
        class="mt-10 flex w-full items-center justify-center"
      >
        <!-- style="width: 1286px" -->

        <div
          class="flex h-[calc(100vh-100px)] max-w-[1286px] flex-col gap-5 overflow-scroll rounded-md bg-white px-6 py-6 text-left"
        >
          <div class="flex items-center justify-between">
            <h1 class="text-xl font-medium text-steel-1100">Equipment</h1>
            <svg
              v-if="displayDismissButton || showDismissButtonProp"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              @click="onDismiss"
            >
              <path
                d="M18.3646 7.04996L16.9504 5.63574L12.0007 10.5855L7.05093 5.63574L5.63672 7.04996L10.5865 11.9997L5.63672 16.9495L7.05093 18.3637L12.0007 13.4139L16.9504 18.3637L18.3646 16.9495L13.4149 11.9997L18.3646 7.04996Z"
                fill="#6C7C9D"
              />
            </svg>
          </div>
          <EldSectionMessages
            v-if="displayEquipmentBanner"
            :status="bannerType"
          />

          <div
            class="flex flex-row gap-2 overflow-x-auto"
            :class="{ 'flex-nowrap': eldProvidersNotRevoked?.length > 1 }"
          >
            <EldProviderSummary
              v-for="provider in eldProvidersNotRevoked"
              :key="provider.id"
              :eld-provider="provider"
              :total-providers="eldProvidersNotRevoked?.length"
            />
          </div>
          <EquipmentSkeletonTable v-if="isCarrierVehiclesResponseLoading" />
          <EquipmentTable
            v-else
            :data="data"
            :pagination="pagination"
            :is-modal="true"
          />
          <div class="flex items-center justify-end gap-2">
            <ButtonComponent
              v-if="displayDismissButton || showDismissButtonProp"
              @click="onDismiss"
            >
              Dismiss
            </ButtonComponent>
            <ButtonComponent
              variant="secondary"
              @click="onResolveIssues"
            >
              Resolve issues
            </ButtonComponent>
          </div>
        </div>
      </div>
    </section>
  </Modal>
</template>

<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
